import { Component, OnInit, OnDestroy, InjectionToken, Injector, FactoryProvider, AfterViewInit } from "@angular/core";

import { BaseControlComponent } from "../base-control/base-control.component";
import { ControllerFactory } from "../../component-base/controller/controller.factory";
import { DropdownController } from "./controllers/dropdown-controller.interface";
import { DefaultDropdownController } from "./controllers/default-dropdown.controller";
import { GISDropdownController } from "./controllers/GIS-dropdown.controller";
import { GISControllerService } from "../../services/GIS-controller.service";
import { GISSettingsComponent } from "../../../builder/components/state/controls/config/common/controller-selector/settings/gis-settings/gis-settings.component";
import { DomainDefinitionService} from "../../../builder/services/domain-definition.service";

import { BehaviorSubject, Subscription, interval, Observable, of } from "rxjs";
import { map, distinctUntilChanged, tap } from "rxjs/operators";

import { GISConfig } from "../../../gis/models/gis-config.model";

import { LoaderService } from "../../../common/services/loader.service";
import { ContextInteractionService, AlteredContextEvent } from "../../services/context-interaction.service";

let defaultControllerFactory = () => { return new DefaultDropdownController() };
let GISControllerFactory = (gisControllerService: GISControllerService, loaderService: LoaderService) => {
    return new GISDropdownController(gisControllerService) };

export const DEFAULT_CONTROLLER = new InjectionToken<DropdownController>("Default");
export const GIS_CONTROLLER = new InjectionToken <DropdownController>("GIS");

export const DROPDOWN_CONTROLLERS = [
    { provide: DEFAULT_CONTROLLER, useFactory: defaultControllerFactory },
    {
        provide: GIS_CONTROLLER, useFactory: GISControllerFactory,
        deps: [GISControllerService],
        settings: [ GISSettingsComponent ]
    }

];

@Component({
    selector: "app-dropdown",
    templateUrl: "./dropdown.component.html",
    styleUrls: ["./dropdown.component.scss"],
    providers: [...DROPDOWN_CONTROLLERS],
    standalone: false
})
export class DropdownComponent extends BaseControlComponent implements OnInit, AfterViewInit, OnDestroy {
    static componentName = "DropdownComponent";
    config: GISConfig;
    controllers: FactoryProvider[] = DROPDOWN_CONTROLLERS;
    controller: DropdownController;

    public selectedItem: any;
    public items: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private subscriptions = new Subscription();
    public idProperty: string;
    public displayProperty: string;
    private sourcePath: string;
    private firstChange: boolean = true;

    get selectedValue(): string {
        let value: string = "";
        let options = this.items.value;
        if (this.contextValue) {
            let selected = options.find(o => o[this.idProperty] === this.contextValue);
            if (selected != null) value = selected[this.displayProperty];
        }
        return value;
    }

    constructor(public _injector: Injector,
        public readonly domainService: DomainDefinitionService,
        public readonly contextService: ContextInteractionService) {
        super(contextService);
        this.config = window["GIS.Conf"] as GISConfig;
        this.controller = ControllerFactory.createController<DropdownController>(DEFAULT_CONTROLLER, this._injector, DROPDOWN_CONTROLLERS).controller;
    }

    public ngOnInit(): void {
        super.ngOnInit();

        if (this.isEdit || !this.componentData) return;
        if (this.componentData.config) {
            if (this.componentData.config.customSource && this.componentData.config.customSource.enabled) {
                this.sourcePath = this.componentData.config.customSource.path;
                this.idProperty = this.componentData.config.customSource.idProperty;
                this.displayProperty = this.componentData.config.customSource.displayProperty;
                const listen$ = this.contextService.contextTargetHasBeenSet.pipe(
                    map((evt: AlteredContextEvent) => {
                        if (evt.target === this.sourcePath || evt.target == `${this.sourcePath}.value`) {
                            if (Array.isArray(evt.value) && this.idProperty && this.displayProperty) {
                                const blankIndex = evt.value.findIndex(e => e[this.idProperty] === "");
                                if (blankIndex === -1) {
                                    let blank = {};
                                    blank[this.idProperty] = "";
                                    blank[this.displayProperty] = "";
                                    evt.value.unshift(blank);
                                }
                                return evt.value;
                            } else {
                                return [evt.value];
                            }

                        }
                        return this.items.value;
                    }),
                    distinctUntilChanged((o: any, n: any) => {
                        if (o && n) {
                            if (o.length === n.length) {
                                if (o.length > 1) {
                                    return o[1][this.idProperty] === n[1][this.idProperty];
                                } else {
                                    return true;
                                }
                            }
                        }
                        return false;
                    })
                ).subscribe(
                    (data: any[]) => {
                        this.items.next(data);
                    },
                    (error) => { console.error(error); }
                );
                this.subscriptions.add(listen$);
            } else {
                this.idProperty = this.componentData.config.idProperty;
                this.displayProperty = this.componentData.config.displayProperty;
            }
            const data$ = this.getInitialData()
                .pipe(map((d: any[]) => {
                    if (this.idProperty && this.displayProperty) {
                        const blankIndex = d.findIndex(e => e[this.idProperty] === "");
                        if (blankIndex === -1) {
                            let blank = {};
                            blank[this.idProperty] = "";
                            blank[this.displayProperty] = "";
                            d.unshift(blank);
                        }                        
                    }
                    return d;
                }))
                .subscribe(
                    (data: any[]) => {
                        this.items.next(data);
                    },
                    (error) => { console.error(error); }
            );
            this.subscriptions.add(data$);          
        }
    }

    ngAfterViewInit() {
        if (this.controller && this.controller.afterViewInit)
            this.controller.afterViewInit();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }

    getInitialData(): Observable<any> {
        let obs$: Observable<any>;
        if (this.componentData.config.customSource && this.componentData.config.customSource.enabled) {
            const data = this.contextService.getContextData(this.sourcePath, this.componentContext);
            if (data != null) {
                const src = Array.from(this.contextService.getContextData(this.sourcePath, this.componentContext));
                obs$ = of(src);
            } else {
                obs$ = of([]);
            }
            
        } else {
            obs$ = this.domainService.get(this.componentData.config.domain).pipe(
                map((data) => {
                    let src = Array.from(data.values);                   
                    return src;
                })
            );
        }

        return obs$;
    }

    change() {
        if (this.controller && this.controller.change) {
            if (this.firstChange) {
                this.firstChange = false; //dropdowns fire their change event on page load even when the page hasn't been visited before.  keeps controller call from executing on that instance
            } else {
                this.controller.change();
            }
            
        }
        super.change();
    }
}
